import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Card, CardContent, CircularProgress, Grid, Typography,
    IconButton, TextField, Dialog, DialogContent,
    DialogActions, Tooltip, Autocomplete
} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import safeToast from "utils/safeToast";
import { format, parseISO } from "date-fns";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { API_ENDPOINTS } from "config/api";
import { decryptText } from "config/encryption";

const ChildrenDetails = () => {
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formMode, setFormMode] = useState("add");
    const [editChild, setEditChild] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [genderOptions, setGenderOptions] = useState([]);
    const [formData, setFormData] = useState({
        childName: "",
        genderId: null,
        genderName: "",
        dob: "",
        rasi: "",
        star: "",
    });
    const [errors, setErrors] = useState({});

    const navigate = useNavigate();

    const convertToInputDateFormat = (dateStr) => {
        if (!dateStr || dateStr.includes("-")) return dateStr;
        const [dd, mm, yyyy] = dateStr.split("/");
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatDateToDDMMYYYY = (dateString) => {
        try {
            return dateString ? format(parseISO(dateString), "dd/MM/yyyy") : "";
        } catch {
            return "";
        }
    };

    const handleSessionExpired = () => {
        safeToast.error("Session expired. Please log in again.");
        localStorage.clear();
        navigate("/BasicAuth");
    };

    const fetchChildrenDetails = useCallback(async () => {
        try {
            const userDetails = localStorage.getItem("UserLogDetails");
            const decryptedUser = decryptText(userDetails);
            if (!decryptedUser) return handleSessionExpired();
            const { StudentId, Token } = JSON.parse(decryptedUser);

            const response = await axios.get(
                API_ENDPOINTS.PROFILE.GET_CHILDREN_INFO(StudentId),
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${Token}`,
                    },
                }
            );

            setChildren(response.data);
        } catch (error) {
            console.error(error);
            safeToast.error("Failed to fetch children details.");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchDropdownData = useCallback(async () => {
        try {
            const userDetails = localStorage.getItem("UserLogDetails");
            const decryptedUser = decryptText(userDetails);
            if (!decryptedUser) return handleSessionExpired();
            const { Token } = JSON.parse(decryptedUser);

            const response = await axios.get(
                API_ENDPOINTS.PROFILE.GET_DROPDOWNS,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${Token}`,
                    },
                }
            );

            setGenderOptions(response.data?.genders || []);
        } catch (error) {
            console.error("Gender dropdown error", error);
            handleSessionExpired();
        }
    }, []);

    useEffect(() => {
        fetchChildrenDetails();
        fetchDropdownData();
    }, [fetchChildrenDetails, fetchDropdownData]);

    const handleAddClick = () => {
        setFormMode("add");
        setEditChild(null);
        setFormData({ childName: "", genderId: null, genderName: "", dob: "", rasi: "", star: "" });
        setFormOpen(true);
    };

    const handleEditClick = (child) => {
        setFormMode("edit");
        setEditChild(child);
        setFormData({
            childName: child.childName || "",
            genderId: child.gender,
            genderName: child.genderName || "",
            dob: convertToInputDateFormat(child.dob),
            rasi: child.rasi || "",
            star: child.star || "",
        });
        setFormOpen(true);
    };

    const handleFormClose = () => {
        setFormOpen(false);
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.childName) newErrors.childName = "Child Name is required";
        if (!formData.genderId) newErrors.genderId = "Gender is required";
        if (!formData.dob) newErrors.dob = "Date of Birth is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        const { childName, genderId, dob } = formData;

        try {
            const userDetails = localStorage.getItem("UserLogDetails");
            const decryptedUser = decryptText(userDetails);
            if (!decryptedUser) return handleSessionExpired();

            const { StudentId, Token, LoginId } = JSON.parse(decryptedUser);
            const genderCode = genderOptions.find((g) => g.id === genderId)?.id || 3;
            const formattedDOB = formatDateToDDMMYYYY(dob);

            const payload = {
                studentId: StudentId,
                childName,
                petName: childName,
                gender: genderCode,
                dob: formattedDOB,
                tob: "Test",
                star: formData.star,
                rasi: formData.rasi,
                childId: formMode === "edit" && editChild ? editChild.id : 0,
                createdUserId: StudentId,
                loginId: LoginId || 0,
            };

            await axios.post(
                API_ENDPOINTS.PROFILE.MERGE_CHILDREN_INFO,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${Token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            safeToast.success(formMode === "edit" ? "Child updated successfully" : "Child added successfully");
            fetchChildrenDetails();
            handleFormClose();
        } catch (error) {
            console.error("Save failed", error);
            safeToast.error("Failed to save child");
        }
    };

    return (
        <>
            <ToastContainer />
            <MDBox py={2} px={3}>
                <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                    <MDBox
                        variant="gradient"
                        bgColor="info"
                        borderRadius="lg"
                        coloredShadow="info"
                        mx={2}
                        mt={-3}
                        p={2}
                        mb={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <MDTypography variant="h6" color="white">
                            Children Details
                        </MDTypography>
                        <Tooltip title="Add Child">
                            <IconButton onClick={handleAddClick}>
                                <Add sx={{ color: "#ffffff" }} />
                            </IconButton>
                        </Tooltip>
                    </MDBox>

                    <CardContent sx={{ backgroundColor: "#fafafa" }}>
                        {loading ? (
                            <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
                                <CircularProgress color="info" />
                            </MDBox>
                        ) : children.length > 0 ? (
                            <Grid container spacing={2}>
                                {children.map((child, index) => (
                                    <Grid item xs={12} key={index}>
                                        <MDBox
                                            p={2}
                                            border="1px solid #e0e0e0"
                                            borderRadius="lg"
                                            bgcolor="#f5f5f5"
                                            position="relative"
                                        >
                                            <IconButton
                                                size="small"
                                                sx={{ position: "absolute", top: 8, right: 8 }}
                                                onClick={() => handleEditClick(child)}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {child.childName}
                                            </Typography>
                                            <Typography variant="body2">Gender: {child.genderName}</Typography>
                                            <Typography variant="body2">DOB: {child.dob}</Typography>
                                            <Typography variant="body2">Rasi: {child.rasi}</Typography>
                                            <Typography variant="body2">Star: {child.star}</Typography>
                                        </MDBox>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <MDBox textAlign="center" py={3}>
                                <MDTypography variant="body2" color="textSecondary">
                                    No children details found. Click the &apos;+&apos; icon to add.
                                </MDTypography>
                            </MDBox>
                        )}
                    </CardContent>
                </Card>

                {/* Form Dialog */}
                <Dialog open={formOpen} onClose={handleFormClose} fullWidth maxWidth="sm">
                    <MDBox bgColor="info.main" color="white" p={2} borderTopRadius="md">
                        <MDTypography variant="h5" fontWeight="medium">
                            {formMode === "edit" ? `Update Child: ${formData.childName}` : "Add New Child"}
                        </MDTypography>
                    </MDBox>

                    <DialogContent dividers sx={{ px: 3, py: 2 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Child Name"
                                    value={formData.childName}
                                    onChange={(e) => setFormData({ ...formData, childName: e.target.value })}
                                    error={!!errors.childName}
                                    helperText={errors.childName}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={genderOptions}
                                    getOptionLabel={(option) => option.ftxt || ""}
                                    value={genderOptions.find((opt) => opt.id === formData.genderId) || null}
                                    onChange={(e, newVal) =>
                                        setFormData({
                                            ...formData,
                                            genderId: newVal?.id || null,
                                            genderName: newVal?.ftxt || "",
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select Gender" variant="outlined" error={!!errors.genderId} helperText={errors.genderId} />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Date of Birth"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={formData.dob}
                                    onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                                    error={!!errors.dob}
                                    helperText={errors.dob}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Rasi"
                                    value={formData.rasi}
                                    onChange={(e) => setFormData({ ...formData, rasi: e.target.value })}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Star"
                                    value={formData.star}
                                    onChange={(e) => setFormData({ ...formData, star: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
                        <MDButton variant="outlined" color="error" onClick={handleFormClose}>
                            Cancel
                        </MDButton>
                        <MDButton variant="gradient" color="info" onClick={handleSave} disabled={!formData.childName || !formData.genderId || !formData.dob}>
                            {formMode === "edit" ? "Update Child" : "Save Child"}
                        </MDButton>
                    </DialogActions>
                </Dialog>
            </MDBox>
        </>
    );
};

export default ChildrenDetails;
